import React from 'react'
import { Helmet } from 'react-helmet'
import { Location } from '@reach/router'
import favicon16x16 from '../../images/favicon/favicon-16x16.png'
import favicon32x32 from '../../images/favicon/favicon-32x32.png'
import appleTouchIcon from '../../images/favicon/apple-touch-icon.png'

const PageHelmet = ({
  alternateUrl,
  title,
  description,
  keywords,
  image,
  url,
  children,
  article,
  disableAds,
  robots,
  lang,
}) => {
  const {
    updatedDate,
    createdDate,
    subcategory,
    publishedAt,
    firstPublishedAt,
  } = article || {}
  let imagePath = 'https://www.datocms-assets.com/7756/1686294926-prt.png'
  if (image) {
    imagePath = image
  }

  const structuredArticleJSON = article
    ? `{
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": ${JSON.stringify(title)},
    "image": [
      ${JSON.stringify(imagePath)}
     ],
    "datePublished": "${firstPublishedAt}",
    "dateModified": "${publishedAt}",
    "author": [{
        "@type": "Person",
        "name": "Edward Mellet"
      }]
  }`
    : ''

  const enAlternateUrl = alternateUrl?.find((item) => item.language === 'en')

  const deduplicateAlternateUrls = (urls) => {
    const deduplicated = {}

    urls.forEach((item) => {
      if (
        !deduplicated[item.language] ||
        deduplicated[item.language].priority < item.priority
      ) {
        deduplicated[item.language] = item
      }
    })

    return Object.values(deduplicated)
  }

  const deduplicatedUrls = deduplicateAlternateUrls(alternateUrl)

  return (
    <>
      <Location>
        {({ location }) => {
          return (
            <Helmet>
              {title && [
                <link
                  rel="alternate"
                  hrefLang={lang || 'en'}
                  href={`${process.env.GATSBY_SITE_BASE_URL}${location.pathname}`}
                />,
                enAlternateUrl?.language === 'en' ? (
                  <link
                    rel="alternate"
                    hrefLang="x-default"
                    key={enAlternateUrl.alternateUrl}
                    href={enAlternateUrl.alternateUrl}
                  />
                ) : null,
                <title
                  {...{
                    key: 'title',
                    itemProp: 'name',
                  }}
                >
                  {title}
                </title>,
                <meta
                  {...{
                    key: 'metaTitle',
                    name: 'title',
                    content: title,
                  }}
                />,
                <meta
                  {...{
                    key: 'ogTitle',
                    property: 'og:title',
                    content: title,
                  }}
                />,
                <meta
                  {...{
                    key: 'twitterTitle',
                    name: 'twitter:title',
                    content: title,
                  }}
                />,
                <meta
                  {...{
                    key: 'ogImage',
                    property: 'og:image',
                    content: imagePath,
                  }}
                />,
                <meta
                  {...{
                    key: 'ogImageWidth',
                    property: 'og:image:width',
                    content: 1200,
                  }}
                />,
                <meta
                  {...{
                    key: 'ogImageHeight',
                    property: 'og:image:height',
                    content: 630,
                  }}
                />,
                <meta
                  {...{
                    key: 'twitterImage',
                    name: 'twitter:image',
                    content: imagePath,
                  }}
                />,
                <meta
                  {...{
                    key: 'twitterCard',
                    name: 'twitter:card',
                    content: 'summary_large_image',
                  }}
                />,
                <meta
                  {...{
                    key: 'image',
                    itemProp: 'image',
                    content: imagePath,
                  }}
                />,
                <meta
                  {...{
                    key: 'name',
                    itemProp: 'name',
                    content: title,
                  }}
                />,
              ]}
              <meta name="article:published_time" content={publishedAt} />
              <meta property="article:modified_time" content={updatedDate} />
              <meta property="article:section" content={subcategory} />
              <meta property="og:type" content="article" />
              {url && <meta property="og:url" content={url} />}
              {url && <link rel="canonical" href={url} />}
              {keywords && <meta name="keywords" content={keywords} />}
              {robots && <meta name="robots" content={robots} />}
              {deduplicatedUrls &&
                deduplicatedUrls.map((item) => (
                  <link
                    rel="alternate"
                    hrefLang={item.language}
                    key={item.alternateUrl}
                    href={item.alternateUrl}
                  />
                ))}
              <link
                {...{
                  rel: 'apple-touch-icon',
                  sizes: '180x180',
                  href: appleTouchIcon,
                }}
              />
              <link
                {...{
                  rel: 'icon',
                  type: 'image/png',
                  sizes: '32x32',
                  href: favicon32x32,
                }}
              />
              <link
                {...{
                  rel: 'icon',
                  type: 'image/png',
                  sizes: '16x16',
                  href: favicon16x16,
                }}
              />
              <link
                rel="preconnect"
                href="https://fonts.gstatic.com"
                crossOrigin
              />
              <link rel="preconnect" href="https://www.datocms-assets.com" />
              <link rel="preconnect" href="https://www.googletagmanager.com" />
              <link rel="preconnect" href="https://scripts.mediavine.com/" />
              <link
                href="https://fonts.googleapis.com/css2?family=Amethysta&family=Nunito+Sans&family=PT+Sans:wght@400;700&display=swap&subset=latin-ext"
                rel="preload"
                as="style"
                type="text/css"
              />
              <meta
                httpEquiv="Content-Security-Policy"
                content="block-all-mixed-content"
              />
              {description && (
                <meta
                  {...{
                    key: 'description',
                    name: 'description',
                    content: description,
                  }}
                />
              )}
              {description && (
                <meta
                  {...{
                    key: 'ogDescription',
                    property: 'og:description',
                    content: description,
                  }}
                />
              )}
              <meta
                {...{
                  key: 'twitterDescription',
                  name: 'twitter:description',
                  content: description || 'WikiJob',
                }}
              />
              {disableAds && <meta name="disableAds" content />}
              {article && (
                <script
                  type="application/ld+json"
                  dangerouslySetInnerHTML={{
                    __html: `
                ${JSON.stringify({
                  '@context': 'https://schema.org',
                  publisher: {
                    '@type': 'Organization',
                    name: 'WikiJob',
                    logo: {
                      '@type': 'ImageObject',
                      url: `${process.env.GATSBY_SITE_BASE_URL}/wikijob.png`,
                    },
                  },
                  '@type': 'Article',
                  headline: title,
                  description,
                  keywords: title,
                  mainEntityOfPage: url,
                  url,
                  datePublished: createdDate,
                  dateModified: updatedDate,
                  articleSection: subcategory,
                  image: [imagePath],
                })}
                `,
                  }}
                />
              )}
              {structuredArticleJSON && (
                <script type="application/ld+json">
                  {structuredArticleJSON}
                </script>
              )}

              {children && children}
            </Helmet>
          )
        }}
      </Location>
    </>
  )
}

export default PageHelmet
